import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
	DebtInvestmentJournalEntryFormView,
	DebtJournalEntryScheduleMonthView,
} from '../../models/debt-management/DebtInvestmentJournalEntryFormView';
import { UtilityService } from '../utility.service';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';

@Injectable({
	providedIn: 'root',
})
export class DebtInvestmentJournalEntryService {
	private readonly urlPath = `${environment.apiUrl}debt-management/clients/`;
	private readonly headers = new HttpHeaders({
		'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
	});

	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		const url = `${
			this.urlPath
		}${clientId}/forms/debt-investment-je-form/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	getSchedule(
		clientId: string,
		journalEntryType: string,
		formId: string | null = null,
		journalEntryCategory: string | null = null
	): Observable<DebtJournalEntryScheduleMonthView[]> {
		const query = [`journalEntryType=${encodeURIComponent(journalEntryType)}`];
		if (formId) {
			query.push(`formId=${formId}`);
		}
		if (journalEntryCategory) {
			query.push(`journalEntryCategory=${encodeURIComponent(journalEntryCategory)}`);
		}
		return this.httpClient.get<DebtJournalEntryScheduleMonthView[]>(
			`${this.urlPath}${clientId}/forms/debt-investment-je-form/schedule?${query.join('&')}`,
			{ headers: this.headers }
		);
	}

	createForm(
		clientId: string,
		request: DebtInvestmentJournalEntryFormView,
		files: File[] = []
	): Observable<DebtInvestmentJournalEntryFormView> {
		return this.httpClient.post<DebtInvestmentJournalEntryFormView>(
			`${this.urlPath}${clientId}/forms/debt-investment-je-form`,
			this.buildFormData(request, files)
		);
	}

	updateForm(
		clientId: string,
		id: string,
		request: DebtInvestmentJournalEntryFormView,
		files: File[] = []
	): Observable<DebtInvestmentJournalEntryFormView> {
		return this.httpClient.put<DebtInvestmentJournalEntryFormView>(
			`${this.urlPath}${clientId}/forms/debt-investment-je-form/${id}`,
			this.buildFormData(request, files)
		);
	}

	getForm(clientId: string, id: string): Observable<DebtInvestmentJournalEntryFormView> {
		return this.httpClient.get<DebtInvestmentJournalEntryFormView>(
			`${this.urlPath}${clientId}/forms/debt-investment-je-form/${id}`,
			{ headers: this.headers }
		);
	}

	submitForApproval(clientId: string, id: string): Observable<DebtInvestmentJournalEntryFormView> {
		return this.httpClient.post<DebtInvestmentJournalEntryFormView>(
			`${this.urlPath}${clientId}/forms/debt-investment-je-form/${id}/approval`,
			{}
		);
	}

	sendForReview(clientId: string, id: string): Observable<DebtInvestmentJournalEntryFormView> {
		return this.httpClient.post<DebtInvestmentJournalEntryFormView>(
			`${this.urlPath}${clientId}/forms/debt-investment-je-form/${id}/review`,
			{}
		);
	}

	rejectForm(
		clientId: string,
		id: string,
		rejection: { reason: string; workflowId: number }
	): Observable<DebtInvestmentJournalEntryFormView> {
		return this.httpClient.post<DebtInvestmentJournalEntryFormView>(
			`${this.urlPath}${clientId}/forms/debt-investment-je-form/${id}/decline`,
			rejection
		);
	}

	approveForm(clientId: string, id: string, userId: string): Observable<DebtInvestmentJournalEntryFormView> {
		return this.httpClient.post<DebtInvestmentJournalEntryFormView>(
			`${this.urlPath}${clientId}/forms/debt-investment-je-form/${id}/approve`,
			{ userId }
		);
	}

	uploadFile(clientId: string, id: string, file: File): Observable<HttpEvent<unknown>> {
		const formData = new FormData();
		formData.append('files', file, file.name);
		return this.httpClient.post<unknown>(
			`${this.urlPath}${clientId}/forms/debt-investment-je-form/${id}/docs`,
			formData,
			{ reportProgress: true, observe: 'events' }
		);
	}

	deleteDocument(clientId: string, formId: string, documentId: string): Observable<void> {
		return this.httpClient.delete<void>(
			`${this.urlPath}${clientId}/forms/debt-investment-je-form/${formId}/docs/${documentId}`
		);
	}

	private buildFormData(request: DebtInvestmentJournalEntryFormView, files: File[]): FormData {
		const formData = new FormData();
		formData.append('model', JSON.stringify(request));
		files.forEach((file) => formData.append('files', file, file.name));
		return formData;
	}
}
